/**
 * Money on screen. The API sends amounts as decimal strings ("12500.0000");
 * they are formatted here with decimal.js, never through a float, so what the
 * user reads is exactly what the books hold (BR-08, NFR-10: ₱1,234,567.89).
 */
import Decimal from 'decimal.js';

export { Decimal };

export function dec(value: string | number | null | undefined): Decimal {
  if (value === null || value === undefined || value === '') return new Decimal(0);
  try {
    return new Decimal(value);
  } catch {
    return new Decimal(0);
  }
}

function group(integer: string): string {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** 1234567.891 → "1,234,567.89" (half-up to 2 places). */
export function amount(value: string | number | null | undefined, blankZero = false): string {
  if (value === null || value === undefined || value === '') return blankZero ? '' : '—';
  const d = dec(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  if (blankZero && d.isZero()) return '';
  const [integer, fraction] = d.abs().toFixed(2).split('.');
  return `${d.isNegative() ? '-' : ''}${group(integer)}.${fraction}`;
}

/** With the peso sign: "₱1,234,567.89", "-₱25.00". */
export function peso(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const text = amount(value);
  return text.startsWith('-') ? `-₱${text.slice(1)}` : `₱${text}`;
}

/** A debit-positive balance shown as "1,000.00 Dr" / "1,000.00 Cr". */
export function drCr(value: string | null | undefined): string {
  const d = dec(value);
  if (d.isZero()) return '0.00';
  return `${amount(d.abs().toString())} ${d.isPositive() ? 'Dr' : 'Cr'}`;
}

/** Normalizes what someone typed ("1,250.5") into "1250.50", or null. */
export function parseAmount(input: string): string | null {
  const clean = input.replace(/[,\s₱]/g, '');
  if (clean === '') return null;
  if (!/^\d+(\.\d{0,4})?$/.test(clean)) return null;
  return new Decimal(clean).toFixed(2);
}
